// covered-by: frontends/carrie/src/routes/ask-puntt/__tests__/askPunttDesktopNotifications.test.ts
import { parseCampaignSearch } from "../campaignSearch";

/** Faster list poll while the tab is hidden and desktop notifications are on. */
export const ASK_PUNTT_UNREAD_HIDDEN_POLL_MS = 10_000;

const PROJECT_CHAT_PATH_RE =
  /^\/ask-puntt\/projects\/[^/]+\/chats\/([^/]+)(?:\/|$)/;
const PERSONAL_CHAT_PATH_RE = /^\/ask-puntt\/([^/]+)(?:\/|$)/;
const PROJECT_WORKSPACE_PATH_RE = /^\/ask-puntt\/projects\/[^/]+(?:\/|$)/;

const RESERVED_ASK_PUNTT_SEGMENTS = new Set([
  "chats",
  "projects",
  "campaigns",
  "streaming-test",
  "style-guide",
]);

export type UnreadChatListRow = {
  _id: string;
  title?: string | null;
  hasUnread?: boolean;
  lastMessageAt?: string;
  projectId?: string | null;
};

export type UnreadChatSnapshot = {
  chatId: string;
  title: string;
  hasUnread: boolean;
  lastMessageAt?: string;
  projectId?: string | null;
};

export function isDesktopNotificationSupported(): boolean {
  return (
    typeof window !== "undefined" &&
    "Notification" in window &&
    typeof Notification.requestPermission === "function"
  );
}

export function getDesktopNotificationPermission():
  | NotificationPermission
  | "unsupported" {
  if (!isDesktopNotificationSupported()) return "unsupported";
  return Notification.permission;
}

export async function requestDesktopNotificationPermission(): Promise<
  NotificationPermission | "unsupported"
> {
  if (!isDesktopNotificationSupported()) return "unsupported";
  if (Notification.permission === "granted") return "granted";
  if (Notification.permission === "denied") return "denied";
  return Notification.requestPermission();
}

export function buildUnreadChatSnapshots(
  chats: readonly UnreadChatListRow[],
): Map<string, UnreadChatSnapshot> {
  const map = new Map<string, UnreadChatSnapshot>();
  for (const chat of chats) {
    map.set(chat._id, {
      chatId: chat._id,
      title: chat.title?.trim() || "Ask Puntt chat",
      hasUnread: chat.hasUnread === true,
      lastMessageAt: chat.lastMessageAt,
      projectId: chat.projectId ?? null,
    });
  }
  return map;
}

export function findNewlyUnreadChats(
  previous: Map<string, UnreadChatSnapshot>,
  next: Map<string, UnreadChatSnapshot>,
  options: {
    activeChatId?: string | null;
    lastNotifiedAtByChat: ReadonlyMap<string, string>;
  },
): UnreadChatSnapshot[] {
  const newlyUnread: UnreadChatSnapshot[] = [];
  for (const snapshot of Array.from(next.values())) {
    if (!snapshot.hasUnread) continue;
    if (
      options.activeChatId != null &&
      snapshot.chatId === options.activeChatId
    ) {
      continue;
    }

    const prev = previous.get(snapshot.chatId);
    const becameUnread = prev?.hasUnread !== true;
    const lastMessageAdvanced =
      snapshot.lastMessageAt != null &&
      snapshot.lastMessageAt !== prev?.lastMessageAt;
    if (!becameUnread && !lastMessageAdvanced) continue;

    const alreadyNotifiedForMessage =
      snapshot.lastMessageAt != null &&
      options.lastNotifiedAtByChat.get(snapshot.chatId) ===
        snapshot.lastMessageAt;
    if (alreadyNotifiedForMessage) continue;

    newlyUnread.push(snapshot);
  }
  return newlyUnread;
}

export function resolveActiveAskPunttChatIdFromPathname(
  pathname: string,
): string | null {
  const projectChatMatch = pathname.match(PROJECT_CHAT_PATH_RE);
  if (projectChatMatch?.[1]) return projectChatMatch[1];

  const personalMatch = pathname.match(PERSONAL_CHAT_PATH_RE);
  const segment = personalMatch?.[1];
  if (segment != null && !RESERVED_ASK_PUNTT_SEGMENTS.has(segment)) {
    return segment;
  }
  return null;
}

export function resolveActiveAskPunttChatId(location: {
  pathname: string;
  search: Record<string, unknown>;
}): string | null {
  const fromPath = resolveActiveAskPunttChatIdFromPathname(location.pathname);
  if (fromPath != null) return fromPath;

  if (!PROJECT_WORKSPACE_PATH_RE.test(location.pathname)) return null;
  const parsed = parseCampaignSearch(location.search);
  return parsed.chatId ?? null;
}

export function buildAskPunttChatNotificationPath(
  snapshot: UnreadChatSnapshot,
): {
  to: string;
  search?: Record<string, string>;
} {
  if (snapshot.projectId) {
    return {
      to: `/ask-puntt/projects/${snapshot.projectId}`,
      search: { tab: "chats", chatId: snapshot.chatId },
    };
  }
  return { to: `/ask-puntt/${snapshot.chatId}` };
}

export function showAskPunttChatDesktopNotification(
  snapshot: UnreadChatSnapshot,
  onNavigate: (snapshot: UnreadChatSnapshot) => void,
): void {
  if (!isDesktopNotificationSupported()) return;
  if (Notification.permission !== "granted") return;

  const notification = new Notification(snapshot.title, {
    body: "New message in Ask Puntt",
    tag: `ask-puntt-chat-${snapshot.chatId}`,
    icon: "/favicon.ico",
  });

  notification.onclick = () => {
    window.focus();
    notification.close();
    onNavigate(snapshot);
  };
}
